"use client";
import {
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  TableColumnDefinition,
  Textarea,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Delete20Regular,
  Link16Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import { useApiGet } from "@skanska/vsa-client-auth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import { useGetUserAccessLevel } from "../hooks/useGetUserAccessLevel";
import { useAdminDeleteTool } from "../hooks/useTools";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ClientRoutes } from "../models/ClientRoutes";
import PagedResult from "../models/PagedResult";
import { Tool } from "../models/Tool";
import { VsaaColorTokens } from "../style/VsaaColorTokens";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  searchContainer: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "end",
  },
  searchInput: {
    minWidth: "300px",
  },
  resultRow: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  resultLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: "100px",
  },
  deleteButton: {
    backgroundColor: VsaaColorTokens.color_destructive_button_background,
    ":hover, :focus, :active": {
      backgroundColor:
        VsaaColorTokens.color_destructive_button_background_hover,
      color: VsaaColorTokens.color_destructive_button_text,
    },
  },
  cellWrap: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  clickableRow: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  content: {
    overflow: "auto",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
});

function LinkManagementPage() {
  const classes = useClasses();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [deleteComment, setDeleteComment] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: searchResults, isLoading: searchLoading } = useApiGet<
    PagedResult<Tool>
  >(
    `${ApiEndpoints.ToolAdminSearchLinks}?query=${encodeURIComponent(activeQuery)}&page=1&pageSize=20`,
    null,
    `admin-link-search-${activeQuery}`
  );

  const { mutateAsync: adminDelete, isPending: deleteIsPending } =
    useAdminDeleteTool(selectedTool?.toolId ?? "");

  const accessLevel = useGetUserAccessLevel();

  if (accessLevel > -1 && accessLevel < 5) {
    navigate("/");
  }

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setActiveQuery(searchQuery.trim());
      setSelectedTool(null);
      setDeleteSuccess(null);
      setDeleteError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDelete = async () => {
    if (!selectedTool?.toolId || !deleteComment.trim()) return;

    try {
      await adminDelete({ comment: deleteComment });
      setDeleteSuccess(selectedTool.title);
      setSelectedTool(null);
      setActiveQuery("");
      setSearchQuery("");
      setDeleteComment("");
      setDeleteDialogOpen(false);
    } catch {
      setDeleteError(t("linkManagement.deleteError"));
      setDeleteDialogOpen(false);
    }
  };

  const handleCloseInfoDialog = () => {
    setSelectedTool(null);
    setDeleteComment("");
  };

  const columns: TableColumnDefinition<Tool>[] = [
    createTableColumn<Tool>({
      columnId: "type",
      renderHeaderCell: () => t("type"),
      renderCell: () => (
        <Tooltip content={t("link")} relationship="label">
          <Link16Regular />
        </Tooltip>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "title",
      renderHeaderCell: () => t("title"),
      renderCell: (item) => item.title,
    }),
    createTableColumn<Tool>({
      columnId: "adminName",
      renderHeaderCell: () => t("linkManagement.adminName"),
      renderCell: (item) => item.adminDisplayName ?? "",
    }),
    createTableColumn<Tool>({
      columnId: "toolId",
      renderHeaderCell: () => t("linkManagement.toolId"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>{item.toolId ?? ""}</span>
      ),
    }),
    createTableColumn<Tool>({
      columnId: "url",
      renderHeaderCell: () => t("linkManagement.url"),
      renderCell: (item) => (
        <span className={classes.cellWrap}>
          <Link
            href={item.webUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {item.webUrl}
          </Link>
        </span>
      ),
    }),
  ];

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Button
          icon={<ArrowLeft20Regular />}
          appearance="subtle"
          aria-label={t("back")}
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        />
        <h2>{t("linkManagement.title")}</h2>
      </div>

      <div className={classes.content}>
        <div className={classes.searchContainer}>
          <Field label={t("linkManagement.searchLabel")}>
            <Input
              className={classes.searchInput}
              placeholder={t("linkManagement.searchPlaceholder")}
              value={searchQuery}
              onChange={(_, data) => setSearchQuery(data.value)}
              onKeyDown={handleKeyDown}
              contentAfter={<Search20Regular />}
            />
          </Field>
          <ButtonWithLoading
            onClick={handleSearch}
            isLoading={searchLoading && !!activeQuery}
            text={t("linkManagement.search")}
          />
        </div>

        {deleteSuccess && (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>
                {t("linkManagement.deleteSuccess")}
              </MessageBarTitle>
              {t("linkManagement.deleteSuccessMessage", {
                title: deleteSuccess,
              })}
            </MessageBarBody>
          </MessageBar>
        )}

        {deleteError && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{t("linkManagement.error")}</MessageBarTitle>
              {deleteError}
            </MessageBarBody>
          </MessageBar>
        )}

        {searchLoading && activeQuery && <Spinner label={t("loading")} />}

        {!searchLoading &&
          activeQuery &&
          searchResults &&
          searchResults.items.length > 0 && (
            <DataGrid
              items={searchResults.items}
              columns={columns}
              getRowId={(item) => item.versionId}
              resizableColumns
              columnSizingOptions={{
                type: { idealWidth: 40, minWidth: 40 },
                title: { idealWidth: 220, minWidth: 120 },
                adminName: { idealWidth: 180, minWidth: 120 },
                toolId: { idealWidth: 280, minWidth: 150 },
                url: { idealWidth: 360, minWidth: 180 },
              }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<Tool>>
                {({ item, rowId }) => (
                  <DataGridRow<Tool>
                    key={rowId}
                    className={classes.clickableRow}
                    onClick={() => setSelectedTool(item)}
                  >
                    {({ renderCell }) => (
                      <DataGridCell>{renderCell(item)}</DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          )}

        {!searchLoading &&
          activeQuery &&
          searchResults &&
          searchResults.items.length === 0 && (
            <MessageBar intent="warning">
              <MessageBarBody>
                {t("linkManagement.noResults", { query: activeQuery })}
              </MessageBarBody>
            </MessageBar>
          )}
      </div>

      <Dialog
        open={!!selectedTool}
        onOpenChange={(_, data) => {
          if (!data.open) handleCloseInfoDialog();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("linkManagement.linkInfo")}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              {selectedTool && (
                <>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>{t("type")}:</span>
                    <span>
                      <Link16Regular />
                    </span>
                  </div>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>{t("title")}:</span>
                    <span>{selectedTool.title}</span>
                  </div>
                  {selectedTool.adminDisplayName && (
                    <div className={classes.resultRow}>
                      <span className={classes.resultLabel}>
                        {t("linkManagement.adminName")}:
                      </span>
                      <span>{selectedTool.adminDisplayName}</span>
                    </div>
                  )}
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>
                      {t("linkManagement.toolId")}:
                    </span>
                    <span>{selectedTool.toolId}</span>
                  </div>
                  <div className={classes.resultRow}>
                    <span className={classes.resultLabel}>
                      {t("linkManagement.url")}:
                    </span>
                    <Link
                      href={selectedTool.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedTool.webUrl}
                    </Link>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleCloseInfoDialog}>
                {t("cancel")}
              </Button>
              <Button
                className={classes.deleteButton}
                icon={<Delete20Regular />}
                appearance="primary"
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t("linkManagement.deleteLink")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        modalType="alert"
        open={deleteDialogOpen}
        onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("linkManagement.deleteDialogTitle")}</DialogTitle>
            <DialogContent>
              <p>
                {t("linkManagement.deleteDialogMessage", {
                  title: selectedTool?.title,
                })}
              </p>
              <Field label={t("linkManagement.deleteCommentLabel")} required>
                <Textarea
                  value={deleteComment}
                  onChange={(_, data) => setDeleteComment(data.value)}
                  placeholder={t("linkManagement.deleteCommentPlaceholder")}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteDialogOpen(false)}
              >
                {t("cancel")}
              </Button>
              <ButtonWithLoading
                onClick={handleDelete}
                isLoading={deleteIsPending}
                text={t("linkManagement.confirmDelete")}
                disabled={!deleteComment.trim()}
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

export default LinkManagementPage;
